/**
 * YouTube-only watch → privacy-enhanced embed URL (share SEO + HTML paint).
 * Vimeo / TikTok / other hosts return null. No request-time oEmbed.
 */

const YOUTUBE_EMBED_PREFIX = 'https://www.youtube-nocookie.com/embed/';

export function resolveYouTubeEmbedUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  const host = parsed.hostname.toLowerCase();
  const isYt =
    host === 'youtube.com' ||
    host === 'www.youtube.com' ||
    host === 'm.youtube.com' ||
    host === 'youtu.be' ||
    host === 'www.youtu.be' ||
    host === 'www.youtube-nocookie.com' ||
    host === 'youtube-nocookie.com';
  if (!isYt) return null;

  let id = parsed.searchParams.get('v') || '';
  if (!id && (host === 'youtu.be' || host === 'www.youtu.be')) {
    id = parsed.pathname.split('/').filter(Boolean)[0] || '';
  }
  if (!id && parsed.pathname.includes('/embed/')) {
    id = parsed.pathname.split('/embed/')[1]?.split('/')[0] || '';
  }
  if (!id && parsed.pathname.includes('/shorts/')) {
    id = parsed.pathname.split('/shorts/')[1]?.split('/')[0] || '';
  }
  if (!id) return null;
  return `${YOUTUBE_EMBED_PREFIX}${id}`;
}

export function youtubeVideoIdFromUrl(url: string | null | undefined): string | null {
  const embed = resolveYouTubeEmbedUrl(url);
  if (!embed) return null;
  const id = embed.slice(YOUTUBE_EMBED_PREFIX.length).split(/[/?#]/)[0] ?? '';
  return id || null;
}

export function youtubeWatchUrlFromId(id: string): string {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`;
}

export function youtubeThumbnailUrlFromId(id: string): string {
  return `https://i.ytimg.com/vi/${encodeURIComponent(id)}/hqdefault.jpg`;
}

export function isYouTubeHref(url: string | null | undefined): boolean {
  return youtubeVideoIdFromUrl(url) != null;
}

/** Soft-launch containd.space consent blob (`containd_lists_consent_v2`). */
export type ContaindSiteConsent = {
  necessary?: boolean;
  analytics?: boolean;
  /**
   * Third-party video iframes (YouTube nocookie). Independent of analytics.
   * Missing / false = fail closed (packaged poster + consent tap, no iframe).
   */
  embeds?: boolean;
  acceptedAt?: string;
};

/** True only when the visitor explicitly allowed embeds — never analytics-only. */
export function containdConsentAllowsEmbeds(
  consent: ContaindSiteConsent | null | undefined,
): boolean {
  return consent?.embeds === true;
}
